import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import { IEmployeeAppInboxResponse } from '@dadohr/shared/domainTypes/experience';
import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  ButtonBase,
  InputAdornment,
  Tooltip,
  Typography
} from '@material-ui/core';
import assertNever from 'assert-never';
import { partition } from 'lodash';
import { CheckCircle, Mail, Newspaper, Search, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChipFilterWithDropdown } from '../../../../../../../components/ChipFilterWithDropdown';
import { TableSearch } from '../../../../../../../components/Table';
import { StandardDrawer } from '../../../../../../../features/Experiences/components/StandardDrawer';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { LightMdToJsx } from '../../../../../../../services/formatter';
import { useProductAnalyticsV2 } from '../../../../../../features/Experience/services/productAnalytics';
import { RoundIconContainer } from '../../RoundIconContainer';
import { TopNavBar } from '../../TopNav';
import {
  inboxDataToMatchCandidates,
  InboxMatchCandidate,
  InboxMatchedItem,
  inboxSearchItemToHref,
  searchInInbox
} from './service';

const DrawerInputContainer = styled(TopNavBar)((p) => ({
  paddingLeft: p.theme.spacing(2),
  paddingRight: p.theme.spacing(2)
}));

const DrawerBodyContainer = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  paddingTop: p.theme.spacing(1)
}));

const ResultsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

const ResultsHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const ItemContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(1)}px`,
  cursor: 'pointer',
  ':hover': {
    background: p.theme.custom.colors.gray.w100
  },
  borderBottom: `${p.theme.custom.border.standard}`
}));

const ShowMoreButtonContainer = styled('div')((p) => ({
  color: p.theme.custom.colors.blue.w400
}));

const SEARCH_ICON_SIZE = 20;

const MINIMUM_SEARCH_LENGTH = 3;

const getIconProps = (
  d: InboxMatchedItem,
  theme: Theme,
  t: UnsafeTFunction
): {
  backgroundShade: 'light' | 'main' | 'dark';
  tooltip: string;
} => {
  switch (d.type) {
    case 'USER':
      return {
        backgroundShade: 'main',
        tooltip: `${t('Person')}`
      };
    case 'TASK':
      return {
        backgroundShade: 'light',
        tooltip: `${t('Task')}`
      };
    case 'MSG':
      return {
        backgroundShade: 'light',
        tooltip: `${t('Message')}`
      };
    case 'MOMENT':
      return {
        backgroundShade: 'light',
        tooltip: `${t('News')}`
      };
    default:
      return assertNever(d);
  }
};

const ItemIcon = ({ d }: { d: InboxMatchedItem }) => {
  const iconSize = 16;
  if (d.type === 'MSG') {
    return <Mail size={iconSize} />;
  }

  if (d.type === 'TASK') {
    // Wrong icon?
    return <CheckCircle size={iconSize} />;
  }

  if (d.type === 'USER') {
    return <User size={iconSize} />;
  }
  if (d.type === 'MOMENT') {
    return <Newspaper size={iconSize} />;
  }

  return null;
};

export const InboxSearchItem = ({
  d,
  onSelect,
  onShowMoreResults,
  iconProps
}: {
  d: InboxMatchedItem;
  onSelect: () => void;
  onShowMoreResults?: () => void;
  iconProps: {
    tooltip: string;
    backgroundShade: 'light' | 'main' | 'dark';
  };
}) => {
  const { t } = useUnsafeTranslation();
  const [showAllResults, setShowAllResults] = useState(false);
  const matches = d.matches;
  const matchesToShow = showAllResults ? matches : matches.slice(0, 1);
  return (
    <ItemContainer role="button" onClick={onSelect}>
      <FlexContainer justifyContent="flex-start" alignItems="flex-start">
        <div>
          <Tooltip title={iconProps.tooltip}>
            <RoundIconContainer
              size="small"
              backgroundShade={iconProps.backgroundShade}
            >
              <ItemIcon d={d} />
            </RoundIconContainer>
          </Tooltip>
        </div>
        <FlexContainer spacing={1} direction="column" alignItems="start">
          {!!matches.length && (
            <FlexContainer spacing={0.5} direction="column" alignItems="start">
              {matchesToShow.map((m, i) => (
                <LightMdToJsx key={i}>{m.withContext.txt}</LightMdToJsx>
              ))}
              {(d.type === 'TASK' || d.type === 'MSG') && (
                <Typography variant="caption">
                  {d.item.templateTitle}
                </Typography>
              )}
              {!showAllResults && matches.length > 1 && (
                <ShowMoreButtonContainer>
                  <ButtonBase
                    onClick={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      onShowMoreResults?.();
                      setShowAllResults(true);
                    }}
                  >
                    {t('+ {{ count }} more {{ result }}', {
                      count: matches.length - 1,
                      result:
                        matches.length - 1 === 1 ? t('result') : t('results')
                    })}
                  </ButtonBase>
                </ShowMoreButtonContainer>
              )}
            </FlexContainer>
          )}
        </FlexContainer>
      </FlexContainer>
    </ItemContainer>
  );
};

export const useInboxSearchResults = ({
  q,
  minimumSearchLengthReached,
  candidates,
  types,
  onSearch
}: {
  q: string;
  minimumSearchLengthReached: boolean;
  candidates: InboxMatchCandidate[];
  types: ('USER' | 'TASK' | 'MSG' | 'MOMENT')[];
  onSearch: (q: string, noOfResults: number) => void;
}) => {
  return useMemo(() => {
    if (!minimumSearchLengthReached) {
      return { results: EMPTY_ARR, hasUpcoming: false, allResultsCount: 0 };
    }
    const nextResults = searchInInbox(candidates, q, {
      contextLength: 54,
      filterByType: types
    });
    onSearch(q, nextResults.length);
    return {
      results: nextResults,
      allResultsCount: nextResults.length
    };
  }, [candidates, q, minimumSearchLengthReached, onSearch, types]);
};

export const InboxSearchResultsList = ({
  q,
  results,
  allResultsCount,
  onSelectMatch,
  onMoreResultsClicked,
  itemToHref,
  theme,
  t
}: {
  q: string;
  results: InboxMatchedItem[];
  allResultsCount: number;
  onSelectMatch: (
    match: InboxMatchedItem,
    i: number,
    q: string,
    allResultsCount: number
  ) => void;
  onMoreResultsClicked: (match: InboxMatchedItem, i: number) => void;
  itemToHref?: (item: InboxMatchedItem) => string;
  theme: Theme;
  t: UnsafeTFunction;
}) => {
  return (
    <>
      {results.map((d, i) => {
        const body = (
          <InboxSearchItem
            key={i}
            d={d}
            onSelect={() => onSelectMatch(d, i, q, allResultsCount)}
            onShowMoreResults={() => onMoreResultsClicked(d, i)}
            iconProps={getIconProps(d, theme, t)}
          />
        );
        const href = itemToHref?.(d);
        return href ? (
          <Link key={i} to={href}>
            {body}
          </Link>
        ) : (
          body
        );
      })}
    </>
  );
};

export const SearchWithinInboxDrawer = ({
  data,
  open,
  onClose,
  onSelectMatch,
  itemToHref,
  onSearch = NOOP,
  onMoreResultsClicked = NOOP,
  onFilter
}: {
  data: IEmployeeAppInboxResponse;
  open: boolean;
  onClose: () => void;
  onSelectMatch: (
    match: InboxMatchedItem,
    i: number,
    q: string,
    noOfResults: number
  ) => void;
  itemToHref?: (item: InboxMatchedItem) => string;
  onSearch?: (q: string, noOfResults: number) => void;
  onMoreResultsClicked?: (match: InboxMatchedItem, i: number) => void;
  onFilter?: (
    type: 'TYPE',
    selected: {
      key: string;
      label: string;
    }[],
    deselected: { key: string; label: string }[]
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const [q, setQ] = useState(''); // better to store in url - but introduces race conditions again...
  const minimumSearchLengthReached = q.length >= MINIMUM_SEARCH_LENGTH;

  const theme = useTheme();

  const typeOptions: {
    key: 'USER' | 'TASK' | 'MSG' | 'MOMENT';
    label: string;
  }[] = useMemo(
    () => [
      { key: 'USER', label: t('Person') },
      { key: 'MSG', label: t('Message') },
      { key: 'TASK', label: t('Task') },
      { key: 'MOMENT', label: t('News') }
    ],
    [t]
  );

  const [selectedType, setSelectedType] = useState(typeOptions);
  const types = useMemo(() => selectedType.map((a) => a.key), [selectedType]);

  const candidates = useMemo(() => inboxDataToMatchCandidates(data), [data]);

  const { results, allResultsCount } = useInboxSearchResults({
    q,
    minimumSearchLengthReached,
    candidates,
    types,
    onSearch
  });

  return (
    <StandardDrawer open={open} onClose={onClose} noGutter>
      <DrawerInputContainer>
        <TableSearch
          term={q}
          onChange={setQ}
          autoFocus={true}
          fullWidth
          placeholder=""
          className={css({
            borderRadius: 20
          })}
          InputProps={{
            className: css({
              '&&': {
                borderRadius: 20
              }
            }),
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  size={SEARCH_ICON_SIZE}
                  color={theme.palette.primary.main}
                />
              </InputAdornment>
            )
          }}
        />
      </DrawerInputContainer>
      <DrawerBodyContainer>
        <FlexContainer direction="column" alignItems="flex-start">
          <FlexContainer>
            <ChipFilterWithDropdown
              label={t('Filter by type')}
              options={typeOptions}
              selectedOptions={selectedType}
              onChange={(nextSelected) => {
                if (onFilter) {
                  const [selected, deselected] = partition(typeOptions, (o) =>
                    nextSelected.find((x) => x.key === o.key)
                  );
                  onFilter('TYPE', selected, deselected);
                }
                setSelectedType(nextSelected);
              }}
            />
          </FlexContainer>
        </FlexContainer>
        <ResultsContainer>
          <ResultsHeading>
            {!results.length && (
              <FlexContainer
                justifyContent="center"
                marginTop={4}
                marginBottom={4}
              >
                <Typography variant="caption">
                  <i>
                    {minimumSearchLengthReached
                      ? t('No matches found')
                      : t('Please type at least {{ minCount }} characters', {
                          minCount: MINIMUM_SEARCH_LENGTH
                        })}
                  </i>
                </Typography>
              </FlexContainer>
            )}
            <InboxSearchResultsList
              q={q}
              results={results}
              allResultsCount={allResultsCount}
              onSelectMatch={onSelectMatch}
              onMoreResultsClicked={onMoreResultsClicked}
              itemToHref={itemToHref}
              theme={theme}
              t={t}
            />

            {!!results.length && (
              <FlexContainerVertical
                alignItems="center"
                justifyContent="center"
                marginTop={2}
                spacing={0.5}
              >
                <Typography variant="caption">
                  {t(`Archived experiences won't appear in search results.`)}
                </Typography>
                <Typography variant="caption">
                  {t(`You can find these on your Dashboard.`)}
                </Typography>
              </FlexContainerVertical>
            )}
          </ResultsHeading>
        </ResultsContainer>
      </DrawerBodyContainer>
    </StandardDrawer>
  );
};

export const SearchWithinInboxButtonBase = ({
  data,
  onSelectMatch,
  itemToHref,
  closeOnSelect
}: {
  data: IEmployeeAppInboxResponse;
  onSelectMatch: (match: InboxMatchedItem) => void;
  itemToHref?: (item: InboxMatchedItem) => string;
  closeOnSelect?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEventV2 } = useProductAnalyticsV2(data.userAccessToken);
  const theme = useTheme();
  const {
    dialogOpen: drawerOpen,
    openDialog: openDrawer,
    closeDialog: closeDrawer
  } = useDialogState();

  const trackingFns = useMemo(
    () => ({
      onSearch: (q: string, noOfResults: number) => {
        // TODO
      },

      onSelect: (
        match: InboxMatchedItem,
        i: number,
        q: string,
        noOfResults: number
      ) => {
        // TODO
      }
    }),
    [trackEventV2]
  );

  return (
    <>
      <ButtonBase
        className={css({
          '&&': {
            background: theme.custom.colors.gray.w100,
            color: theme.palette.primary.main,
            padding: `${theme.spacing()}px ${theme.spacing(2)}px`,
            borderRadius: 20,
            minWidth: 208,
            justifyContent: 'flex-start'
          }
        })}
        onClick={() => {
          // trackingFns.onOpen();
          openDrawer();
        }}
      >
        <Search size={SEARCH_ICON_SIZE} />
      </ButtonBase>
      <SearchWithinInboxDrawer
        data={data}
        open={drawerOpen}
        onClose={closeDrawer}
        onSelectMatch={(match, i, q, allResultsCount) => {
          trackingFns.onSelect(match, i, q, allResultsCount);
          onSelectMatch(match);
          if (closeOnSelect) {
            closeDrawer();
          }
        }}
        onSearch={trackingFns.onSearch}
        itemToHref={itemToHref}
      />
    </>
  );
};

export const useInboxSearchSelectMatchTrackFn = (userAccessToken: string) => {
  const { trackEventV2 } = useProductAnalyticsV2(userAccessToken);
  return (m: InboxMatchedItem) => {
    const resultType =
      m.type === 'USER'
        ? 'User'
        : m.type === 'TASK'
        ? 'Task'
        : m.type === 'MSG'
        ? 'Message'
        : '';
    if (resultType) {
      trackEventV2({
        eventType: PAEventTypeV2.OTHER,
        name: 'Search Result Clicked',
        Explanation: 'User clicked on a search result',
        'Result Type': resultType,
        Archived: 'No' // No archived search implemented yet
      });
    } else {
      console.warn('Unknown tracking result type for', m);
    }
  };
};

export const SearchWithinInboxButton = ({
  data
}: {
  data: IEmployeeAppInboxResponse;
}) => {
  const onSelectMatch = useInboxSearchSelectMatchTrackFn(data.userAccessToken);
  return (
    <SearchWithinInboxButtonBase
      data={data}
      onSelectMatch={onSelectMatch}
      itemToHref={(d) => inboxSearchItemToHref(data.userAccessToken, d)}
      closeOnSelect
    />
  );
};
